import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CountrySelect from '../components/CountrySelect';
import { translations } from '../translations';
import registerImage from '../assets/images/register-image.png';
import '../styles/pages/Auth.css';
import { addUsers } from '../contexts/db.User';

function Register({ language }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('+33');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptNews, setAcceptNews] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);

  const navigate = useNavigate();
  
  const t = translations[language];

  // Validate email format
  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Validate password requirements
  const isValidPassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return re.test(password);
  };

  // Validate phone number
  const isValidPhone = (phone) => {
    return phone.length >= 6; // Simple validation, adjust as needed
  };

  // Handle field blur to mark as touched
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  // Validate form fields
  const validateForm = () => {
    const errors = {};
    
    // Validate required fields
    if (!firstName.trim()) errors.firstName = t.requiredField;
    if (!lastName.trim()) errors.lastName = t.requiredField;
    
    if (!email) {
      errors.email = t.requiredField;
    } else if (!isValidEmail(email)) {
      errors.email = t.invalidEmail;
    }
    
    if (!phone) {
      errors.phone = t.requiredField;
    } else if (!isValidPhone(phone)) {
      errors.phone = t.invalidPhone;
    }
    
    if (!password) {
      errors.password = t.requiredField;
    } else if (password.length < 8) {
      errors.password = t.passwordTooShort;
    } else if (!isValidPassword(password)) {
      errors.password = t.passwordRequirementsNotMet;
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = t.requiredField;
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t.passwordsDoNotMatch;
    }
    
    if (!acceptTerms) {
      errors.acceptTerms = t.termsNotAccepted;
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Real-time validation for individual fields
  const validateField = (field, value) => {
    const errors = { ...fieldErrors };
    
    switch (field) {
      case 'firstName':
        if (!value.trim()) {
          errors.firstName = t.requiredField;
        } else {
          delete errors.firstName;
        }
        break;
      case 'lastName':
        if (!value.trim()) {
          errors.lastName = t.requiredField;
        } else {
          delete errors.lastName;
        }
        break;
      case 'email':
        if (!value) {
          errors.email = t.requiredField;
        } else if (!isValidEmail(value)) {
          errors.email = t.invalidEmail;
        } else {
          delete errors.email;
        }
        break;
      case 'phone':
        if (!value) {
          errors.phone = t.requiredField;
        } else if (!isValidPhone(value)) {
          errors.phone = t.invalidPhone;
        } else {
          delete errors.phone;
        }
        break;
      case 'password':
        if (!value) {
          errors.password = t.requiredField;
        } else if (value.length < 8) {
          errors.password = t.passwordTooShort;
        } else if (!isValidPassword(value)) {
          errors.password = t.passwordRequirementsNotMet;
        } else {
          delete errors.password;
        }
        // Also validate confirm password if it has a value
        if (confirmPassword && value !== confirmPassword) {
          errors.confirmPassword = t.passwordsDoNotMatch;
        } else if (confirmPassword && value === confirmPassword) {
          delete errors.confirmPassword;
        }
        break;
      case 'confirmPassword':
        if (!value) {
          errors.confirmPassword = t.requiredField;
        } else if (password !== value) {
          errors.confirmPassword = t.passwordsDoNotMatch;
        } else {
          delete errors.confirmPassword;
        }
        break;
    }
    
    setFieldErrors(errors);
  };

  // Mark all fields as touched when submitting
  const markAllAsTouched = () => {
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      password: true,
      confirmPassword: true,
      acceptTerms: true
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    markAllAsTouched();
    
    if (validateForm()) {
      createUser();
    } else {
      // Scroll to the first error
      const firstErrorField = Object.keys(fieldErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const createUser = async () => {
    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});
      
      const response = await addUsers(firstName, lastName, email, dialCode, phone, password, acceptNews, acceptTerms);
      console.log('Registration response:', response);
      
      if (response && response.id) {
        // Store token if returned by Laravel API
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        
        // Redirect to the VerificationPending page with the user email, ID, and language
        navigate('/verification-pending', { 
          state: { 
            email: email,
            userId: response.id,
            language: language
          } 
        });
      } else {
        setError(t.registrationPartialSuccess);
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle Laravel validation errors
      if (error.response?.data?.errors) {
        const laravelErrors = error.response.data.errors;
        const mappedErrors = {};
        
        // Map Laravel field names to form field names
        if (laravelErrors.first_name) mappedErrors.firstName = laravelErrors.first_name[0];
        if (laravelErrors.last_name) mappedErrors.lastName = laravelErrors.last_name[0];
        if (laravelErrors.email) mappedErrors.email = laravelErrors.email[0];
        if (laravelErrors.password) mappedErrors.password = laravelErrors.password[0];
        if (laravelErrors.phone_number) mappedErrors.phone = laravelErrors.phone_number[0];
        if (laravelErrors.accept_general_condition) mappedErrors.acceptTerms = laravelErrors.accept_general_condition[0];
        
        setFieldErrors(mappedErrors);
        markAllAsTouched();
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError(t.registrationError);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="register-page">
      {/* Decorative background elements */}
      <div className="register-decorations">
        <div className="decoration decoration-shield">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,5A3,3 0 0,1 15,8A3,3 0 0,1 12,11A3,3 0 0,1 9,8A3,3 0 0,1 12,5M17.13,17C15.92,18.85 14.11,20.24 12,20.92C9.89,20.24 8.08,18.85 6.87,17C6.53,16.5 6.24,16 6,15.47C6,13.82 8.71,12.47 12,12.47C15.29,12.47 18,13.79 18,15.47C17.76,16 17.47,16.5 17.13,17Z" />
          </svg>
        </div>
        <div className="decoration decoration-leaf-left">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" />
          </svg>
        </div>
        <div className="decoration decoration-leaf-right">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" />
          </svg>
        </div>
        <div className="decoration decoration-checkbox">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,5V19H5V5H19M10,17L6,13L7.41,11.58L10,14.17L16.59,7.58L18,9" />
          </svg>
        </div>
        <div className="decoration decoration-mail">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
          </svg>
        </div>
      </div>

      {/* Logo */}
      <div className="register-logo">
        <Link to="/" className="logo-text">DysOrto</Link>
      </div>

      {/* Header */}
      <div className="register-header">
        <h1>{t.createAccount}</h1>
        <p className="register-subtitle">
          {t.welcomeMessage || 'Bienvenue !'} <span className="highlight">{t.signUpPrompt || 'Inscrivez-vous'}</span> {t.accessFeatures || 'pour accéder à toutes les fonctionnalités.'}
        </p>
      </div>

      {/* Form Card */}
      <div className="register-card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t.email} <span className="required-asterisk">*</span></label>
            <input
              type="email"
              id="email"
              placeholder={t.enterEmail}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (touched.email) validateField('email', e.target.value);
              }}
              onBlur={() => {
                handleBlur('email');
                validateField('email', email);
              }}
              className={fieldErrors.email && touched.email ? 'input-error' : ''}
              required
            />
            {fieldErrors.email && touched.email && (
              <div className="field-error">{fieldErrors.email}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">{t.password} <span className="required-asterisk">*</span></label>
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder={t.enterPassword}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (touched.password) validateField('password', e.target.value);
                  if (confirmPassword && touched.confirmPassword) {
                    validateField('confirmPassword', confirmPassword);
                  }
                }}
                onBlur={() => {
                  handleBlur('password');
                  validateField('password', password);
                }}
                className={fieldErrors.password && touched.password ? 'input-error' : ''}
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t.hidePassword : t.showPassword}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  {showPassword ? (
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  ) : (
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  )}
                </svg>
              </button>
            </div>
            {fieldErrors.password && touched.password && (
              <div className="field-error">{fieldErrors.password}</div>
            )}
            <div className="password-requirements">
              {t.passwordRequirements}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t.confirmPassword} <span className="required-asterisk">*</span></label>
            <div className="password-input">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                placeholder={t.confirmPassword}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (touched.confirmPassword) validateField('confirmPassword', e.target.value);
                  if (password && touched.password) {
                    validateField('password', password);
                  }
                }}
                onBlur={() => {
                  handleBlur('confirmPassword');
                  validateField('confirmPassword', confirmPassword);
                }}
                className={fieldErrors.confirmPassword && touched.confirmPassword ? 'input-error' : ''}
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? t.hidePassword : t.showPassword}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  {showConfirmPassword ? (
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  ) : (
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  )}
                </svg>
              </button>
            </div>
            {fieldErrors.confirmPassword && touched.confirmPassword && (
              <div className="field-error">{fieldErrors.confirmPassword}</div>
            )}
          </div>

          <div className="name-row">
            <div className="form-group">
              <label htmlFor="firstName">{t.firstName} <span className="required-asterisk">*</span></label>
              <input
                type="text"
                id="firstName"
                placeholder={t.enterFirstName}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (touched.firstName) validateField('firstName', e.target.value);
                }}
                onBlur={() => {
                  handleBlur('firstName');
                  validateField('firstName', firstName);
                }}
                className={fieldErrors.firstName && touched.firstName ? 'input-error' : ''}
                required
              />
              {fieldErrors.firstName && touched.firstName && (
                <div className="field-error">{fieldErrors.firstName}</div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="lastName">{t.lastName} <span className="required-asterisk">*</span></label>
              <input
                type="text"
                id="lastName"
                placeholder={t.enterLastName}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (touched.lastName) validateField('lastName', e.target.value);
                }}
                onBlur={() => {
                  handleBlur('lastName');
                  validateField('lastName', lastName);
                }}
                className={fieldErrors.lastName && touched.lastName ? 'input-error' : ''}
                required
              />
              {fieldErrors.lastName && touched.lastName && (
                <div className="field-error">{fieldErrors.lastName}</div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t.phoneNumber} <span className="required-asterisk">*</span></label>
            <div className="phone-input">
              <CountrySelect
                value={dialCode}
                onChange={(dialCode) => setDialCode(dialCode.replace('+', '00'))}
              />
              <input
                type="tel"
                id="phone"
                placeholder={t.enterPhone}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (touched.phone) validateField('phone', e.target.value);
                }}
                onBlur={() => {
                  handleBlur('phone');
                  validateField('phone', phone);
                }}
                className={fieldErrors.phone && touched.phone ? 'input-error' : ''}
                required
              />
            </div>
            {fieldErrors.phone && touched.phone && (
              <div className="field-error">{fieldErrors.phone}</div>
            )}
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="acceptNews"
              checked={acceptNews}
              onChange={(e) => setAcceptNews(e.target.checked)}
            />
            <label htmlFor="acceptNews">{t.acceptNewsletters}</label>
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              required
            />
            <label htmlFor="acceptTerms" className={fieldErrors.acceptTerms && touched.acceptTerms ? 'label-error' : ''}>
              {t.acceptTerms} <button type="button" className="terms-link-btn" onClick={() => setShowTermsModal(true)}>{t.termsLink}</button> <span className="required-asterisk">*</span>
            </label>
          </div>
          {fieldErrors.acceptTerms && touched.acceptTerms && (
            <div className="field-error">{fieldErrors.acceptTerms}</div>
          )}

          {error && <div className="error-message">{error}</div>}
          
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? (t.loading || 'Processing...') : (t.signUp || "S'inscrire")}
          </button>
        </form>

        <div className="register-footer">
          <span>{t.alreadyHaveAccount}</span>
          <Link to="/login">{t.login}</Link>
        </div>
      </div>

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="terms-modal-close" onClick={() => setShowTermsModal(false)} aria-label="Close">
              ×
            </button>
            <div className="terms-modal-body">
              <h2>{t.termsTitle}</h2>
              <p className="terms-modal-subtitle">{t.termsSubtitle}</p>

              <section className="terms-section">
                <h3>{t.termsIntroTitle}</h3>
                <p>{t.termsIntroText}</p>
              </section>

              <section className="terms-section">
                <h3>{t.accountTitle}</h3>
                <p>{t.accountText}</p>
                <ul>
                  <li>{t.accountItem1}</li>
                  <li>{t.accountItem2}</li>
                  <li>{t.accountItem3}</li>
                </ul>
              </section>

              <section className="terms-section">
                <h3>{t.usageTitle}</h3>
                <p>{t.usageText}</p>
                <h4>{t.prohibitedTitle}</h4>
                <ul>
                  <li>{t.prohibitedItem1}</li>
                  <li>{t.prohibitedItem2}</li>
                  <li>{t.prohibitedItem3}</li>
                  <li>{t.prohibitedItem4}</li>
                </ul>
              </section>

              <section className="terms-section">
                <h3>{t.intellectualPropertyTitle}</h3>
                <p>{t.intellectualPropertyText}</p>
              </section>

              <section className="terms-section">
                <h3>{t.terminationTitle}</h3>
                <p>{t.terminationText}</p>
              </section>

              <section className="terms-section">
                <h3>{t.limitationTitle}</h3>
                <p>{t.limitationText}</p>
              </section>

              <section className="terms-section">
                <h3>{t.governingLawTitle}</h3>
                <p>{t.governingLawText}</p>
              </section>

              <section className="terms-section">
                <h3>{t.changesTitle}</h3>
                <p>{t.changesText}</p>
              </section>

              <section className="terms-section">
                <h3>{t.contactTermsTitle}</h3>
                <p>{t.contactTermsText}</p>
                <p>Email: <a href="mailto:support@dysorto.fr">support@dysorto.fr</a></p>
              </section>

              <p className="terms-last-updated">{t.lastUpdated}</p>
            </div>
            <div className="terms-modal-footer">
              <button className="terms-accept-btn" onClick={() => { setAcceptTerms(true); setShowTermsModal(false); }}>
                {t.acceptAndClose || 'Accept & Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register;
